import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  cancelProposedChip,
  proposeAvailableChip,
} from "core-integration/src/store/chips/actions";
import {
  getAvailableChipNames,
  getProposedChipName,
} from "core-integration/src/store/chips/reducers";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { IElementsById } from "core-integration/src/store/elements/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { IEvent } from "core-integration/src/store/events/types";
import { ITransferState } from "core-integration/src/store/my-team/types";
import {
  getFreeTransfers,
  getProposedTransfers,
  getTransferCosts,
  getTransferState,
} from "core-integration/src/store/squad/reducers";
import { makeTransfers as makeTransfersThunk } from "core-integration/src/store/squad/thunks";
import { ITransfersData } from "core-integration/src/store/squad/types";
import { formatRawAsLocalI18n } from "core-integration/src/utils/datetime";
import { dateLocales } from "../../i18n";
import Alert, { AlertGroup, AlertItem } from "../Alert";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import Table from "../Table";
import { useNavigate } from "react-router";

interface OwnProps {
  allowNavigation: () => void;
  handleHide: () => void;
}

const DialogHeading = styled.div`
  padding: ${({ theme }) => theme.space[2]};
  background-color: ${({ theme }) => theme.colors.blueDarker2};
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.impact};
  text-transform: uppercase;
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
`;

const ConfirmTransfersDialog = ({ allowNavigation, handleHide }: OwnProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch>();

  const elementsById: IElementsById = useSelector(getElementsById);
  const freeTransfers = useSelector(getFreeTransfers);
  const nextEvent: IEvent | null = useSelector(getNextEvent);
  const proposedChip = useSelector(getProposedChipName);
  const transferCosts = useSelector(getTransferCosts);
  const transfers: ITransfersData[] = useSelector(getProposedTransfers);
  const transferState: ITransferState | null = useSelector(getTransferState);
  const wildcardAvailable = useSelector(
    (state: RootState) => getAvailableChipNames(state).indexOf("wildcard") > -1,
  );

  if (!nextEvent || !transferState) {
    return null;
  }

  const makeTransfers = () => dispatch(makeTransfersThunk());
  const cancelChip = (chip: string) => dispatch(cancelProposedChip(chip));
  const proposeChip = (chip: string) => dispatch(proposeAvailableChip(chip));

  const submitTransfers = async () => {
    allowNavigation();
    await makeTransfers();
    navigate("/my-team");
  };

  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <DialogHeading>
          {t("confirmTransfers.heading", "Confirm Transfers")}
        </DialogHeading>
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <Box mb={4}>
          <Table>
            <thead>
              <tr>
                <th>{t("confirmTransfers.out", "Out")}</th>
                <th>{t("confirmTransfers.in", "In")}</th>
                <th>{t("confirmTransfers.cost", "Cost")}</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t, index) => (
                <tr key={t.element_out}>
                  {/*
                   * Non-null assertion used here because elements
                   * are always transferred out in this game.
                   */}
                  <td>{elementsById[t.element_out!].web_name}</td>
                  <td>{elementsById[t.element_in].web_name}</td>
                  <td>
                    {transferCosts && index >= freeTransfers
                      ? transferState.cost
                      : 0}{" "}
                    pts
                  </td>
                </tr>
              ))}
              <tr>
                <td />
                <td>{t("confirmTransfers.totalCost", "Total cost")}</td>
                <td>{transferCosts} pts</td>
              </tr>
            </tbody>
          </Table>
        </Box>
        <span className="notranslate">
          {transferCosts > 0 ? (
            <Alert type="warning">
              <AlertGroup>
                <AlertItem>
                  {t(
                    "confirmTransfers.transferAlert",
                    `
                  These transfers will be active for ${
                    nextEvent.name
                  } (${formatRawAsLocalI18n(
                    nextEvent.deadline_time,
                    dateLocales[i18n.language],
                  )}) and will deduct ${transferCosts}pts from your score!
                  `,
                    {
                      name: nextEvent.name,
                      time: formatRawAsLocalI18n(
                        nextEvent.deadline_time,
                        dateLocales[i18n.language],
                      ),
                      costs: transferCosts,
                    },
                  )}
                </AlertItem>
              </AlertGroup>
              {wildcardAvailable && proposedChip !== "rich" && (
                <AlertGroup>
                  <AlertItem>
                    {t(
                      "confirmTransfers.playYourWildcard",
                      "Play your wildcard to make unlimited free transfers this Round.",
                    )}
                  </AlertItem>
                  <Button onClick={() => proposeChip("wildcard")} width={1}>
                    {t("confirmTransfers.playWildcard", `Play Wildcard`)}
                  </Button>
                </AlertGroup>
              )}
            </Alert>
          ) : (
            <Alert type="warning">
              <AlertGroup>
                <AlertItem>
                  {t(
                    "confirmTransfers.theseTransfersWill",
                    `These transfers will be active for ${
                      nextEvent.name
                    } (${formatRawAsLocalI18n(
                      nextEvent.deadline_time,
                      dateLocales[i18n.language],
                    )})`,
                    {
                      name: nextEvent.name,
                      time: formatRawAsLocalI18n(
                        nextEvent.deadline_time,
                        dateLocales[i18n.language],
                      ),
                    },
                  )}
                </AlertItem>
              </AlertGroup>
              {proposedChip === "wildcard" && (
                <AlertGroup>
                  <AlertItem>
                    <strong>
                      {t(
                        "confirmTransfers.youAreProposingWildcard",
                        "You are proposing to play your wildcard!",
                      )}
                    </strong>
                  </AlertItem>
                  <AlertItem>
                    {t(
                      "confirmTransfers.cancelYourWildcard",
                      "Cancel your wildcard if you would prefer to spend points on these transfers.",
                    )}
                  </AlertItem>
                  <Button onClick={() => cancelChip("wildcard")} width={1}>
                    {t("confirmTransfers.cancelWildcard", "Cancel Wildcard")}
                  </Button>
                </AlertGroup>
              )}
              {proposedChip === "rich" && (
                <AlertGroup>
                  <AlertItem>
                    <b>
                      {t(
                        "confirmTransfers.proposingRichUncle",
                        "You are proposing to play your Rich Uncle chip! This allows you to make unlimited free transfers with an unlimited budget for a single round. At the next deadline, your squad is returned to how it was at the start of the round.",
                      )}
                    </b>
                  </AlertItem>
                  <AlertItem>
                    {t(
                      "confirmTransfers.cancelYourRichUncle",
                      "Cancel Rich Uncle chip if you would prefer not to do this.",
                    )}
                  </AlertItem>
                  <Button
                    onClick={() => {
                      cancelChip("rich");
                      return handleHide();
                    }}
                    width={1}
                  >
                    {t("confirmTransfers.cancelRichUncle", `Cancel Rich Uncle`)}
                  </Button>
                </AlertGroup>
              )}
            </Alert>
          )}
          <Box mt={4}>
            <div>
              <DialogButtonItem>
                <Button onClick={submitTransfers} width={1}>
                  {t("confirmTransfers.confirmTransfers", "Confirm Transfers")}

                  {transferCosts > 0 && !proposedChip && (
                    <>
                      <br />
                      (-{transferCosts}pts)
                    </>
                  )}
                  {proposedChip === "wildcard" && (
                    <>
                      <br />(
                      {t(
                        "confirmTransfers.andPlayWildcard",
                        "and play Wildcard",
                      )}
                      )
                    </>
                  )}
                  {proposedChip === "rich" && (
                    <>
                      <br />(
                      {t(
                        "confirmTransfers.andPlayRichUncle",
                        "and play Rich Uncle",
                      )}
                      )
                    </>
                  )}
                </Button>
              </DialogButtonItem>
              <DialogButtonItem>
                <Button variant="secondary" onClick={handleHide} width={1}>
                  {t("confirmTransfers.changeTransfers", "Change Transfers")}
                </Button>
              </DialogButtonItem>
            </div>
          </Box>
        </span>
      </Dialog.Body>
    </Dialog>
  );
};

export default ConfirmTransfersDialog;
